import React from 'react';
import {
  InfoCard, StructuredMetadataTable,
} from '@backstage/core-components';
import {
  Alert, Chip, Grid, List, ListItem, ListItemIcon, ListItemText, Tooltip, Typography,
} from '@mui/material';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RdsClusterResourceEntity, RdsRecommendation } from '@eng-portal/czi-extensions-common';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { omit } from 'lodash';
import { CardTitle } from './CardTitle';

const severityColor: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  high: 'error',
  medium: 'warning',
  low: 'info',
  informational: 'default',
};

function RecommendationItem({ rec }: { rec: RdsRecommendation }): JSX.Element {
  return (
    <ListItem alignItems="flex-start">
      <ListItemText
        primary={rec.recommendation || rec.detection || 'Recommendation'}
        secondary={
          <>
            {rec.description && (
              <Typography variant="body2" color="text.secondary" component="span" display="block" sx={{ mb: 0.5 }}>
                {rec.description}
              </Typography>
            )}
            {rec.category && <Chip label={rec.category} size="small" sx={{ mr: 1 }} />}
            {rec.severity && (
              <Chip
                label={rec.severity}
                size="small"
                color={severityColor[rec.severity] ?? 'default'}
              />
            )}
          </>
        }
      />
    </ListItem>
  );
}

export function RdsClusterAboutCard(): JSX.Element {
  const { entity: cluster } = useEntity<RdsClusterResourceEntity>();
  if (!cluster) {
    return <Alert severity="error">RDS cluster not found</Alert>;
  }

  const {
    metadata: { name, title },
    spec,
  } = cluster;

  const metadata: Record<string, any> = {
    ...omit(spec, 'type', 'owner', 'dependsOn', 'awsRecommendations', 'scorecard'),
  };

  const { awsRecommendations } = spec;

  return (
    <InfoCard
      title={<CardTitle title={title || name} />}
    >
      <Grid container spacing={3} alignItems="flex-start">
        <Grid item md={10} xl={11}>
          <List>
            <ListItem>
              <ListItemIcon>
                <Tooltip title="Metadata">
                  <AccountTreeIcon />
                </Tooltip>
              </ListItemIcon>
              <StructuredMetadataTable metadata={metadata} />
            </ListItem>
            <br />
            <ListItem>
              <ListItemIcon>
                <Tooltip title="AWS Recommendations">
                  {awsRecommendations.length > 0
                    ? <ReportProblemIcon color="warning" />
                    : <CheckCircleIcon color="success" />}
                </Tooltip>
              </ListItemIcon>
              {awsRecommendations.length > 0 ? (
                <List disablePadding>
                  <Typography variant="subtitle2" gutterBottom>
                    AWS Recommendations ({awsRecommendations.length})
                  </Typography>
                  {awsRecommendations.map((rec) => (
                    <RecommendationItem key={rec.recommendationId} rec={rec} />
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No active AWS recommendations
                </Typography>
              )}
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </InfoCard>
  );
}
